import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowLeft, ShoppingBag, Sparkles, Filter, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "../App";
import Footer from "../components/Footer";

const CATEGORY_LABELS = {
  all: "All Products",
  sprays: "Aura Sprays",
  incense: "Incense",
  candles: "Candles",
  journals: "Journals",
  digital: "Digital"
};

export default function Store({ auth }) {
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [isPremium, setIsPremium] = useState(false);
  const [cart, setCart] = useState([]);
  const [availableCategories, setAvailableCategories] = useState(["all"]);

  // Compute filtered products directly (no separate state)
  const products = selectedCategory === "all" 
    ? allProducts 
    : allProducts.filter(p => p.category === selectedCategory);

  useEffect(() => {
    fetchAllProducts();
    loadCart();
  }, []);

  const loadCart = () => {
    const savedCart = localStorage.getItem('softRitualCart');
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
  };

  const fetchAllProducts = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/store/products`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        const productsList = data.products || [];
        console.log("Store: Fetched products:", productsList.length);
        
        setAllProducts(productsList);
        setIsPremium(data.is_premium || false);
        
        // Determine which categories have products
        const categoriesWithProducts = new Set(productsList.map(p => p.category));
        const available = ["all", ...Object.keys(CATEGORY_LABELS).filter(
          cat => cat !== "all" && categoriesWithProducts.has(cat)
        )];
        setAvailableCategories(available);
      } else {
        console.error("Store: API returned non-OK status:", response.status);
        toast.error("Failed to load products");
      }
    } catch (error) {
      console.error("Store: Failed to fetch products:", error);
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  const addToCart = (product) => {
    const existingItem = cart.find(item => item.product_id === product.product_id);
    let newCart;
    
    if (existingItem) {
      newCart = cart.map(item => 
        item.product_id === product.product_id 
          ? { ...item, quantity: item.quantity + 1 }
          : item
      );
    } else {
      newCart = [...cart, { 
        product_id: product.product_id, 
        name: product.name,
        price: product.user_price,
        image: product.images?.[0],
        quantity: 1 
      }];
    }
    
    setCart(newCart);
    localStorage.setItem('softRitualCart', JSON.stringify(newCart));
    toast.success(`${product.name} added to cart`);
  };

  const cartItemCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#FDFBF7]/95 backdrop-blur-sm border-b border-[#E6E2DD]">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to={auth?.user ? "/dashboard" : "/"} className="flex items-center gap-2 text-[#4A4744] hover:text-[#2D2A26]">
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">{auth?.user ? "Back" : "Home"}</span>
          </Link>
          
          <h1 
            className="text-2xl font-light text-[#2D2A26]"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Sensory Supports
          </h1>
          
          <div className="flex items-center gap-2">
            {!auth?.user && (
              <>
                <Link to="/track-order">
                  <Button variant="ghost" size="sm" className="text-[#4A4744] hover:text-[#A87C6D] text-sm">
                    Track Order
                  </Button>
                </Link>
                <Link to="/login">
                  <Button variant="ghost" size="sm" className="text-[#4A4744] hover:text-[#A87C6D] text-sm">
                    Sign in
                  </Button>
                </Link>
              </>
            )}
            {auth?.user && (
              <Link to="/orders">
                <Button variant="ghost" size="sm" className="text-[#4A4744] hover:text-[#A87C6D] text-sm">
                  My Orders
                </Button>
              </Link>
            )}
            <Link to="/cart" className="relative">
              <Button variant="ghost" size="icon" className="text-[#4A4744] hover:text-[#A87C6D]">
                <ShoppingBag className="w-5 h-5" />
                {cartItemCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-[#A87C6D] text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                    {cartItemCount}
                  </span>
                )}
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-6xl mx-auto px-6 py-8 w-full">
        {/* Intro */}
        <div className="text-center mb-8">
          <p className="text-[#4A4744] font-light max-w-xl mx-auto">
            Gentle tools to support your nervous system — chosen with care to complement your rituals.
          </p>
          {isPremium && (
            <div className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-[#A87C6D]/10 rounded-full">
              <Sparkles className="w-4 h-4 text-[#A87C6D]" />
              <span className="text-sm text-[#A87C6D]">Inner Circle discounts applied</span>
            </div>
          )}
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap items-center justify-center gap-2 mb-8">
          {availableCategories.map((key) => (
            <button
              key={key}
              onClick={() => setSelectedCategory(key)}
              className={`px-4 py-2 rounded-full text-sm transition-colors ${
                selectedCategory === key
                  ? "bg-[#A87C6D] text-white"
                  : "bg-[#F5F2ED] text-[#4A4744] hover:bg-[#E6E2DD]"
              }`}
            >
              {CATEGORY_LABELS[key]}
            </button>
          ))}
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-[#A87C6D] animate-spin" />
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-[#6E6A66]">No products found in this category.</p>
            <p className="text-xs text-[#ccc] mt-2">Debug: allProducts={allProducts.length}, products={products.length}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map(product => (
              <div 
                key={product.product_id}
                data-testid={`product-card-${product.product_id}`}
                className="bg-white rounded-2xl overflow-hidden border border-[#E6E2DD] hover:shadow-lg transition-shadow"
              >
                {/* Product Image */}
                <Link to={`/store/${product.product_id}`}>
                  <div className="aspect-square bg-[#F5F2ED] relative">
                    {product.images?.[0] ? (
                      <img 
                        src={product.images[0]} 
                        alt={product.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[#6E6A66]">
                        <ShoppingBag className="w-12 h-12" />
                      </div>
                    )}
                    {product.is_exclusive && (
                      <div className="absolute top-3 left-3 px-3 py-1 bg-[#A87C6D] text-white text-xs rounded-full flex items-center gap-1">
                        <Sparkles className="w-3 h-3" />
                        Exclusive
                      </div>
                    )}
                    {product.stock_quantity <= 5 && product.stock_quantity > 0 && product.product_type === "physical" && (
                      <div className="absolute top-3 right-3 px-3 py-1 bg-[#C8553D]/90 text-white text-xs rounded-full">
                        Only {product.stock_quantity} left
                      </div>
                    )}
                    {product.stock_quantity === 0 && product.product_type === "physical" && (
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <span className="text-white font-medium">Out of Stock</span>
                      </div>
                    )}
                  </div>
                </Link>
                
                {/* Product Info */}
                <div className="p-4">
                  <Link to={`/store/${product.product_id}`}>
                    <h3 className="font-medium text-[#2D2A26] mb-1 hover:text-[#A87C6D]">
                      {product.name}
                    </h3>
                  </Link>
                  <p className="text-sm text-[#6E6A66] capitalize mb-3">
                    {product.category}
                    {product.product_type === "digital" && " • Digital Download"}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-medium text-[#2D2A26]">
                        ${product.user_price.toFixed(2)}
                      </span>
                      {product.discount_applied && (
                        <span className="text-sm text-[#6E6A66] line-through">
                          ${product.price.toFixed(2)}
                        </span>
                      )}
                    </div>
                    
                    <Button
                      onClick={() => addToCart(product)}
                      disabled={product.stock_quantity === 0 && product.product_type === "physical"}
                      size="sm"
                      className="rounded-full bg-[#A87C6D] hover:bg-[#956d5f] text-white"
                    >
                      Add
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
